import React from 'react'
import { Users } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import type { OrganizationAccessPolicy, OrganizationAccessRole } from '../../hooks/useDiagramAccessPolicy'

interface OrganizationAccessControlProps {
  policy?: OrganizationAccessPolicy
  disabled: boolean
  saving: boolean
  error: boolean
  onUpdate: (patch: Partial<OrganizationAccessPolicy>) => void
  t: (key: string) => string
}

export const OrganizationAccessControl: React.FC<OrganizationAccessControlProps> = ({
  policy,
  disabled,
  saving,
  error,
  onUpdate,
  t,
}) => {
  const isDisabled = disabled || !policy || saving
  const accessValue: 'none' | OrganizationAccessRole = policy?.orgAccessMode === 'org'
    ? policy.orgAccessRole
    : 'none'

  const handleAccessChange = (value: 'none' | OrganizationAccessRole) => {
    if (value === 'none') {
      onUpdate({ orgAccessMode: 'none' })
      return
    }

    onUpdate({ orgAccessMode: 'org', orgAccessRole: value })
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-4 rounded-lg bg-muted/30 p-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
            <Users className="h-4 w-4 text-primary" />
          </div>
          <p className="truncate text-sm font-medium">{t('share.everyoneInOrganization')}</p>
        </div>
        <Select value={accessValue} onValueChange={handleAccessChange} disabled={isDisabled}>
          <SelectTrigger className="w-36 shrink-0"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">{t('share.noAccess')}</SelectItem>
            <SelectItem value="viewer">{t('share.canView')}</SelectItem>
            <SelectItem value="editor">{t('share.canEdit')}</SelectItem>
            <SelectItem value="moderator">{t('share.canManage')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {error && <p className="text-sm text-destructive">{t('share.organizationAccessError')}</p>}
    </div>
  )
}
